import Head from "next/head";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { CardAdd } from "iconsax-react";
import { useForm, Controller } from "react-hook-form";
import { Box, Button, Grid, InputLabel, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography, useTheme, FormControl } from "@mui/material";

import Pages from "src/constants/pages";
import MainLayout from "src/layouts/mainLayout";
import { getIsSsrMobile } from "src/utils/getIsSsrMobile"
import { useGetAllUserFacility } from "src/services/new-facility";
import DesktopLoading from "src/components/common/desktop-loading";
import { DEFAULT_GIFT_CREDIT_INSTALMENT } from "src/constants/giftCredit";
import DesktopWrapper from "src/components/layout/desktop/desktop-wrapper";
import ValidationHelperText from "src/components/common/validation-helper-text";
import { EnumMemberGiftCreditRequestStatus } from "src/services/gift-credit/payment/types";
import useGiftCreditActiveRequests from "../../gift-credit/hooks/useGiftCreditActiveRequests";

export enum EnumTypes {
	FACILITY = 1,
	CREDIT = 2
};

const LoanRepayment = () => {
	const theme = useTheme()
	const router = useRouter()
	const [paymentType, setPaymentType] = useState<EnumTypes>(EnumTypes.FACILITY)
	const { data: facilityData, isLoading: facilityLoading } = useGetAllUserFacility();
	const { data: creditData, isFetching: creditFetching, isPending: creditPending } = useGiftCreditActiveRequests()
	const { control, handleSubmit, reset, formState: { errors } } = useForm({ defaultValues: { paymentId: "" } })

	const handleChangeReqType = (_event, newType) => {
		if (newType !== null) {
			setPaymentType(newType)
			reset({ paymentId: "" })
		};
	};

	const loading = creditPending || creditFetching || facilityLoading;
	let repaymentList = [];

	if (paymentType === EnumTypes.FACILITY && facilityData?.value) {
		facilityData?.value?.filter(f => f.status === 214).map(x => {
			repaymentList.push({
				id: +x.contractNumber,
				name: `قرارداد ${x.contractNumber} - ${x.amount.toLocaleString()} ریال`
			});
		});
	};

	if (paymentType === EnumTypes.CREDIT && Array.isArray(creditData?.value)) {
		repaymentList = creditData.value.filter(req => req.status === EnumMemberGiftCreditRequestStatus.CHARGED).map(req => ({
			id: req.giftCreditRequestGuid,
			name: `${`قرارداد ${req.contractNumber}`} - ${req.creditAmount.toLocaleString()} ریال`
		}));
	};

	const onSubmit = payload => {
		if (paymentType === EnumTypes.FACILITY) {
			router.push(`/payInstallment/installment/${payload.paymentId}`)
			// router.push(`${Pages.PaymentInstallments}/${data.facilityRequestId}`)
		};
		if (paymentType === EnumTypes.CREDIT) {
			router.push(`${DEFAULT_GIFT_CREDIT_INSTALMENT}/${payload.paymentId}`)
		};
	};

	return (
		<>
			<Head>
				<title>بازپرداخت اقساط من</title>
			</Head>
			<DesktopWrapper title='بازپرداخت اقساط' subTitle='' icon={<CardAdd color={theme.palette.primary.main} />}>
				<Box width='100%' textAlign='center' my={4}>
					<ToggleButtonGroup
						color='primary'
						value={paymentType}
						exclusive
						onChange={handleChangeReqType}
						aria-label='نوع پرداخت'
					>
						<ToggleButton value={EnumTypes.FACILITY}>پرداخت اقساط وام</ToggleButton>
						<ToggleButton value={EnumTypes.CREDIT}>پرداخت اقساط اعتبار </ToggleButton>
					</ToggleButtonGroup>
				</Box>
				{loading ? (
					<DesktopLoading />
				) : (
					<Box width='100%' my={2}>
						<form onSubmit={handleSubmit(onSubmit)}>
							<Typography variant='subtitle1' mb={1}>
								کاربر گرامی، لطفا شماره تسهیلات/اعتبار مورد نظر خود را انتخاب کرده و جهت پرداخت اقساط اقدام نمایید.
							</Typography>
							<Grid container spacing={2} justifyContent='center'>
								<Grid item xs={12} md={9}>
									<FormControl fullWidth error={!!errors.paymentId}>
										<InputLabel id='facility-label'>
											{paymentType === EnumTypes.FACILITY ? "انتخاب شماره تسهیلات" : "انتخاب اعتبار"}
										</InputLabel>
										<Controller
											name='paymentId'
											control={control}
											rules={{
												required:
													paymentType === EnumTypes.FACILITY
														? "انتخاب شماره تسهیلات الزامی است"
														: "انتخاب اعتبار الزامی است"
											}}
											render={({ field }) => (
												<Select
													{...field}
													labelId='facility-label'
													label={paymentType === EnumTypes.FACILITY ? "انتخاب شماره تسهیلات" : "انتخاب اعتبار"}
													disabled={repaymentList.length === 0}
												>
													{repaymentList.map(item => (
														<MenuItem key={item.id} value={item.id}>
															{item.name}
														</MenuItem>
													))}
												</Select>
											)}
										/>
									</FormControl>
								</Grid>
								<Grid item xs={12} md={3}>
									<Button
										type='submit'
										variant='contained'
										fullWidth
										disabled={repaymentList.length === 0}
										sx={{ borderRadius: 2, height: "100%" }}
										disableElevation
									>
										پرداخت قسط
									</Button>
								</Grid>
								<Grid item xs={12}>
									<ValidationHelperText error={!!errors.paymentId} helperText={errors?.paymentId?.message} />
								</Grid>
								{repaymentList.length === 0 && (
									<Grid item xs={12}>
										<Typography color='text.secondary' align='center'>
											گزینه‌ای برای پرداخت یافت نشد.
										</Typography>
									</Grid>
								)}
							</Grid>
						</form>
					</Box>
				)}
			</DesktopWrapper>
		</>
	);
};
LoanRepayment.Layout = MainLayout
export default LoanRepayment


export async function getServerSideProps(context) {
	return {
		props: {
			isSsrMobile: getIsSsrMobile(context)
		}
	}
}
